import { getPagePermission } from './permissions';
import { isRestaurantOperatingContext } from '../utils/operatingContext';

export const MANAGEMENT_ROLES = ['Керівник', 'Адміністратор'];
export const WORKER_ROLE = 'Працівник';
export const WORKER_ROLES = [WORKER_ROLE, 'Кухар', 'Шеф кухар', 'ШефКухар', 'Бармен', 'Посудомийка', 'Прибиральниця'];
export const WORKER_AND_MANAGEMENT_ROLES = [...MANAGEMENT_ROLES, ...WORKER_ROLES];

export const hasManagementAccess = (roles: string[] = [], permissions: string[] = []) => {
  if (permissions.some((permission) => permission === 'page.admin' || permission.startsWith('page.management.') || permission.startsWith('page.analytics.'))) {
    return true;
  }

  return MANAGEMENT_ROLES.some((role) => roles.includes(role));
};

export const hasWorkerAccess = (roles: string[] = [], permissions: string[] = []) => {
  if (permissions.some((permission) => [
    'page.management.semi-finished',
    'page.management.dishes',
    'page.management.event-orders',
    'page.management.preparation-worksheets',
    'page.management.kitchenware',
    'page.management.employees',
    'page.management.inventory',
  ].includes(permission))) {
    return true;
  }

  return WORKER_ROLES.some((role) => roles.includes(role));
};
export const KITCHEN_NAV_ROLES = ['Кухар', 'Шеф кухар', 'ШефКухар', 'Бармен', 'Посудомийка', 'Прибиральниця'];

export type ContextSupport = 'restaurant' | 'system' | 'legacy' | 'unavailable';
export type NavLinkItem = {
  label: string;
  path: string;
  contextSupport: ContextSupport;
  replacementPath?: string;
};
export type NavSection = { title: string; links: NavLinkItem[] };

const navLink = (
  label: string,
  path: string,
  contextSupport: ContextSupport = 'restaurant',
  replacementPath?: string,
): NavLinkItem => ({ label, path, contextSupport, replacementPath });

const PROFILE_LINKS: NavLinkItem[] = [
  navLink('Мій робочий простір', '/workspace'),
  navLink('Профіль', '/profile'),
  navLink('Зарплата', '/salary'),
  navLink('Графік', '/schedule'),
  navLink('Меню', '/menu', 'system'),
  navLink('Бронювання залу', '/book-event', 'system'),
  navLink('Контексти роботи', '/operating-contexts', 'system'),
];

export const NAV_SECTIONS: NavSection[] = [
  {
    title: 'Профіль',
    links: PROFILE_LINKS,
  },
  {
    title: 'Події та клієнти',
    links: [
      navLink('Замовлення подій', '/management/event-orders'),
      navLink('CRM: операційна панель', '/management/crm-dashboard'),
      navLink('CRM клієнтів', '/management/customers'),
      navLink('Готель', '/management/hotel'),
      navLink('Аналітика місць', '/analytics/venues'),
      navLink('Фінанси подій', '/analytics/event-finance'),
    ],
  },
  {
    title: 'Меню та рецептури',
    links: [
      navLink('Товари', '/management/products'),
      navLink('Нутрієнтна база', '/management/nutrition-base'),
      navLink('Страви', '/management/dishes'),
      navLink('Затвердження продажу', '/management/sale-catalog'),
      navLink('Напівфабрикати', '/management/semi-finished'),
      navLink('Додаткові послуги', '/management/additional-services'),
    ],
  },
  {
    title: 'Виробництво та підготовка',
    links: [
      navLink('Виконання роботи', '/management/kitchen-execution'),
      navLink('План кухні (попередній)', '/management/kitchen-work'),
      navLink('Планування виробництва Н/Ф', '/management/semi-finished-production'),
      navLink('Бланки підготовки', '/management/preparation-worksheets'),
      navLink('Операції підготовки', '/management/preparation-operations'),
    ],
  },
  {
    title: 'Склад',
    links: [
      navLink('Інвентар', '/management/inventory'),
      navLink('Залишки', '/management/inventory-balance'),
      navLink('Ревізії', '/management/inventory-revisions'),
      navLink('Прихідні накладні', '/management/inventory-receipts'),
      navLink('Переміщення', '/management/inventory-transfers'),
      navLink('Переміщення між складами', '/management/inventory-warehouse-transfers'),
      navLink('Списання', '/management/inventory-write-offs'),
      navLink('Харчування', '/management/inventory-nutrition'),
      navLink('Пересорт', '/management/inventory-resort'),
      navLink('Посуд (ревізія)', '/management/kitchenware'),
      navLink('Стрічка операцій', '/management/inventory-activity'),
    ],
  },
  {
    title: 'Закупівлі та постачальники',
    links: [
      navLink('Закупівельний лист', '/management/purchase-worksheet'),
      navLink('Замовлення постачальникам', '/management/supplier-purchase-orders'),
      navLink('Планування закупівель', '/management/purchase-planning'),
      navLink('Забезпечення замовлень', '/management/order-supply'),
      navLink('Платіжні реєстри', '/management/supplier-payment-batches'),
      navLink('Фінанси закупівель', '/analytics/procurement-finance'),
    ],
  },
  {
    title: 'Адміністрування',
    links: [
      navLink('HR: персонал, оцінка, підрозділи', '/management/employees'),
      navLink('Зарплата працівників', '/management/salary-payroll', 'legacy', '/management/restaurant-payroll'),
      navLink('Зарплата ресторану', '/management/restaurant-payroll'),
      navLink('Зарплата — Весільні Зали', '/management/halls-payroll', 'legacy', '/management/restaurant-payroll'),
      navLink('Акаунти, ролі, доступ', '/admin/employees', 'system'),
      navLink('Експорт / Імпорт', '/management/export-import'),
    ],
  },
];

export const WORKER_NAV_SECTIONS: NavSection[] = [
  {
    title: 'Профіль',
    links: PROFILE_LINKS.filter(link => ['/workspace', '/profile', '/salary', '/schedule', '/operating-contexts'].includes(link.path)),
  },
  {
    title: 'Кухня та виробництво',
    links: [
      navLink('Виконання роботи', '/management/kitchen-execution'),
      navLink('Мої завдання кухні (попередні)', '/management/kitchen-work'),
      navLink('Напівфабрикати та виробництво', '/management/semi-finished'),
      navLink('Планування виробництва Н/Ф', '/management/semi-finished-production'),
      navLink('Страви', '/management/dishes'),
      navLink('Бланки підготовки', '/management/preparation-worksheets'),
    ],
  },
  {
    title: 'Події',
    links: [
      navLink('Замовлення подій', '/management/event-orders'),
    ],
  },
];

export const ADMIN_NAV_SECTION: NavSection = {
  title: 'Довідники та налаштування',
  links: [
    navLink('Адмін панель', '/admin', 'system'),
    navLink('Облікові періоди', '/management/accounting-periods'),
    navLink('Журнал аудиту', '/admin/audit-logs', 'restaurant'),
    navLink('Категорії', '/management/product-categories'),
    navLink('Постачальники', '/management/suppliers'),
    navLink('Місця проведення', '/management/event-venues'),
    navLink('Бронювання та завдатки', '/management/venue-bookings'),
    navLink('Алергени', '/management/allergens'),
  ],
};

export const canOpenNavigationPath = (
  path: string, permissions: string[], selectedGroupId: string | null = null,
  restaurantAuditReadsEnabled = false,
): boolean => {
  if (path === '/admin/audit-logs' && isRestaurantOperatingContext(selectedGroupId) &&
      !restaurantAuditReadsEnabled) return false;
  const permission = getPagePermission(path);
  return !permission || permissions.includes(permission);
};

export { isRestaurantOperatingContext } from '../utils/operatingContext';

export const getNavigationLink = (path: string, search = ''): NavLinkItem | undefined => {
  const links = [...NAV_SECTIONS, ADMIN_NAV_SECTION, ...WORKER_NAV_SECTIONS]
    .flatMap(section => section.links);
  const link = links.find(item => item.path === path);
  if (path === '/management/employees' && new URLSearchParams(search).get('legacy') === '1') {
    return {
      label: 'Історичний HR',
      path,
      contextSupport: 'legacy',
      replacementPath: '/management/employees',
    };
  }
  return link;
};

export const getVisibleNavigationSections = (
  sections: NavSection[],
  permissions: string[],
  selectedGroupId: string | null = null,
  restaurantAuditReadsEnabled = false,
): NavSection[] =>
  sections.map(section => ({
    ...section,
    links: section.links.filter(link =>
      canOpenNavigationPath(link.path, permissions, selectedGroupId, restaurantAuditReadsEnabled) &&
      (!isRestaurantOperatingContext(selectedGroupId) ||
        !['legacy', 'unavailable'].includes(link.contextSupport))),
  })).filter(section => section.links.length > 0);

export const getNavigationLabel = (path: string): string => {
  const link = getNavigationLink(path);
  if (!link) throw new Error(`Navigation label is not configured for ${path}`);
  return link.label;
};
